import { useEffect, useState } from 'react';
import { Monitor, ChefHat, GlassWater, Plus, Trash2, Loader2, RefreshCw, ShieldCheck, Link2, Smartphone, Download, Tablet, LayoutGrid, ChevronDown, ChevronRight, X } from 'lucide-react';
import { api } from '../lib/spizirnaUi';
import Dropdown from './receptura/Dropdown';
import AplikaceVerze from './AplikaceVerze';
import AplikaceTelefony from './AplikaceTelefony';
import KiosekNastaveni from './KiosekNastaveni';
import ModalShell from './ui/ModalShell';
import usePotvrzeni from './ui/usePotvrzeni';
import useJeMobil from '../hooks/useJeMobil';

const POPIS_OPRAVNENI = {
    stav_hovoru: 'čísla volajících', telefon: 'volání', sms: 'SMS',
    notifikace: 'oznámení', baterie: 'úspora baterie', pres_aplikace: 'zobrazení přes aplikace',
    bluetooth: 'Bluetooth', rizeni_hovoru: 'hovor do sluchátek (adb, viz aplikace)',
};
// Chybí jen tomu telefonu, jehož pokladny mají sluchátko — jinak je nepotřebuje.
const JEN_SE_SLUCHATKY = ['bluetooth', 'rizeni_hovoru'];

// Zobrazení pokladny (2026-10-22) — musí sedět s CisPosTerminal::ZALOZKY / TLACITKA.
const ZALOZKY_POS = [
    { id: 'stoly', nazev: 'Stoly' },
    { id: 'fronta', nazev: 'Fronta' },
    { id: 'casovky', nazev: 'Časovky' },
    { id: 'kds', nazev: 'Kuchyň' },
    { id: 'mapa', nazev: 'Rozvoz' },
    { id: 'telefon', nazev: 'Telefon' },
];
const TLACITKA_POS = [
    { id: 'nova_objednavka', nazev: 'Nová objednávka' },
    { id: 'nova_rezervace', nazev: 'Nová rezervace' },
];

/**
 * Správa → Provozovna → Pokladny a telefon (dřív Nastavení → Pokladny).
 *
 * Telefon pokladny (2026-10-13): každá pokladna má volbu „telefon" — přes ten
 * volá, zavěšuje, posílá SMS a jen jeho hovory a SMS vidí. Jeden telefon může
 * mít víc pokladen; pokladna bez telefonu žádný nemá. Jeden telefon je
 * „výchozí pro SMS" (automatické SMS z rozvozu, volání z OS).
 *
 * Norma (revize Nastavení 2026-10-12): nový terminál v ModalShell místo
 * prompt(), potvrzení přes usePotvrzeni, přepínač místo checkboxu, barva
 * sekce z `barva` (jen text, linka, obrys — pozadí neutrální).
 *
 * Registr terminálů FofrLamaPOS. Terminál se tu založí a dostane kód; ten se
 * naťuká do POSu a počítač se tím spáruje. Tady se mu i přiděluje role
 * kuchyňského displeje (Kuchyně 1/2/3, Bar) — na terminálu se už nevolí.
 *
 * Povinná registrace: co není v registru, neexistuje — POS bez spárování
 * ukáže jen obrazovku s výzvou. Zapíná se až po spárování všech pokladen,
 * do té doby se nespárované jen ukazují dole jako „čeká na registraci".
 */
export default function PosTerminalySprava({ isDark, barva = '#3B82F6' }) {
    const isMobile = useJeMobil();
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const [novy, setNovy] = useState(null);   // { nazev, zarizeni_id, typ? }
    const [data, setData] = useState(null);
    const [stanice, setStanice] = useState([]);
    const [chyba, setChyba] = useState('');
    const [verze, setVerze] = useState([]);
    const [zobrazeniOtevrene, setZobrazeniOtevrene] = useState(null);
    // Sbalené karty terminálů — pamatuje si prohlížeč (2026-10-22).
    const [sbalene, setSbalene] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem('sprava.terminaly.sbalene') || '[]')); } catch { return new Set(); }
    });
    const prepnoutSbaleni = (id) => setSbalene(s => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        try { localStorage.setItem('sprava.terminaly.sbalene', JSON.stringify([...n])); } catch { /* bez úložiště jen do obnovení */ }
        return n;
    });
    const nactiVerze = () => api('/api/aplikace-verze').then(d => setVerze(d.verze ?? [])).catch(() => {});

    const nacti = () => api('/api/pos-terminaly').then(setData).catch(() => setData({ terminaly: [], nezname: [], povinna: false }));
    useEffect(() => {
        nacti();
        nactiVerze();
        api('/api/kds-stanice').then(s => setStanice(s.filter(x => x.aktivni))).catch(() => {});
        // Online stav se mění sám — pokladny se hlásí každých 30 s.
        const t = setInterval(nacti, 15000);
        return () => clearInterval(t);
    }, []);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.05)' : '#fff';
    const kartaBg = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';

    const proved = async (fn) => {
        setChyba('');
        try { await fn(); } catch (e) { setChyba(e?.error || e?.message || 'Nepodařilo se uložit.'); }
        nacti();
    };
    const uprav = (t, zmena) => {
        setData(d => ({ ...d, terminaly: d.terminaly.map(x => x.id === t.id ? { ...x, ...zmena } : x) }));
        return proved(() => api(`/api/pos-terminaly/${t.id}`, 'PUT', zmena));
    };

    if (!data) return <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;

    const pridat = (zarizeni = null) => setNovy({ nazev: `Pokladna ${data.terminaly.length + 1}`, zarizeni_id: zarizeni });
    const zalozit = () => {
        if (!novy?.nazev?.trim()) return;
        const { nazev, zarizeni_id, typ } = novy;
        setNovy(null);
        proved(() => api('/api/pos-terminaly', 'POST', { nazev: nazev.trim(), zarizeni_id, typ: typ || 'pokladna' }));
    };
    const tlacitkoObrys = { background: 'transparent', border: `1px solid ${border}`, color: 'var(--text-primary)', cursor: 'pointer' };
    const telefony = data.terminaly.filter(t => t.typ === 'telefon');
    const maTelefon = telefony.length > 0;
    const pridatTelefon = () => setNovy({ nazev: maTelefon ? `Telefon ${telefony.length + 1}` : 'Telefon bistra', zarizeni_id: null, typ: 'telefon' });
    // Kiosek s lamami — FofrLamaPOS v režimu kiosku, páruje se kódem na kiosku.
    const kiosky = data.terminaly.filter(t => t.typ === 'kiosek');
    const pridatKiosek = () => setNovy({ nazev: kiosky.length ? `Kiosek ${kiosky.length + 1}` : 'Kiosek', zarizeni_id: null, typ: 'kiosek' });

    const hlavni = stanice.find(s => s.druh === 'kuchyne' && s.cislo === 1);

    return (
        <div className="space-y-5 max-w-3xl">
            <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Pokladny a displeje</h3>
                <p className="text-xs mt-1" style={{ color: muted }}>
                    Každý počítač s pokladnou musí být tady. Založ terminál, opiš jeho kód do pokladny (objeví se po přihlášení,
                    nebo v Nastavení pokladny) a terminál se spáruje. Roli kuchyňského displeje nastavuješ tady — platí pro záložku Kuchyň
                    i pro displej na externím monitoru.
                </p>
            </div>

            <div className="flex items-start gap-3 rounded-xl px-4 py-3"
                style={{ background: data.povinna ? 'rgba(16,185,129,0.08)' : kartaBg, border: `1px solid ${data.povinna ? 'rgba(16,185,129,0.4)' : border}` }}>
                <ShieldCheck size={18} style={{ color: data.povinna ? '#10b981' : muted, marginTop: 2 }} />
                <div className="flex-1">
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Povinná registrace</div>
                    <p className="text-xs mt-0.5" style={{ color: muted }}>
                        {data.povinna
                            ? 'Zapnuto — nespárovaný počítač pokladnu ani kuchyň neotevře.'
                            : 'Vypnuto — nespárované počítače fungují a ukazují se dole jako „čeká na registraci". Zapni, až budou spárované všechny.'}
                    </p>
                </div>
                <button onClick={() => proved(() => api('/api/pos-terminaly/povinna', 'PUT', { povinna: !data.povinna }))}
                    className="text-xs font-semibold rounded-lg px-3 py-1.5"
                    style={{ background: data.povinna ? 'transparent' : '#10b981', color: data.povinna ? muted : '#fff',
                        border: `1px solid ${data.povinna ? border : '#10b981'}`, cursor: 'pointer' }}>
                    {data.povinna ? 'Vypnout' : 'Zapnout'}
                </button>
            </div>

            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}

            <div className="space-y-2">
                {data.terminaly.length === 0 && (
                    <p className="text-xs" style={{ color: muted }}>Zatím žádný terminál.</p>
                )}
                {data.terminaly.map(t => {
                    const s = stanice.find(x => x.id === t.kds_stanice_id);
                    const telefon = t.typ === 'telefon';
                    const kiosek = t.typ === 'kiosek';
                    const Ikona = telefon ? Smartphone : kiosek ? Tablet : !t.kds_stanice_id ? Monitor : s?.druh === 'bar' ? GlassWater : ChefHat;
                    const zar = t.stav_zarizeni || {};
                    const sluchatkaPouziva = data.terminaly.some(x => x.telefon_id === t.id && x.sluchatko && x.aktivni);
                    const chybiOpravneni = Object.entries(zar.opravneni || {})
                        .filter(([k, ok]) => !ok && (sluchatkaPouziva || !JEN_SE_SLUCHATKY.includes(k)))
                        .map(([k]) => POPIS_OPRAVNENI[k] || k);
                    const stav = !t.aktivni ? { text: 'vyřazený', barva: muted }
                        : !t.sparovany ? { text: 'čeká na spárování', barva: 'var(--text-secondary)' }
                        : t.online ? { text: 'online', barva: '#10b981' }
                        : { text: 'offline', barva: '#EF4444' };
                    return (
                        <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-3"
                            style={{ background: kartaBg, border: `1px solid ${border}`, opacity: t.aktivni ? 1 : 0.55 }}>
                            <button type="button" onClick={() => prepnoutSbaleni(t.id)} title={sbalene.has(t.id) ? 'Rozbalit' : 'Sbalit'}
                                className="flex items-center justify-center rounded-lg"
                                style={{ width: 30, height: 30, background: 'none', border: 'none', color: muted, cursor: 'pointer', marginLeft: -8 }}>
                                {sbalene.has(t.id) ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                            </button>
                            <Ikona size={18} style={{ color: s?.barva || muted }} />
                            <input value={t.nazev}
                                onChange={e => setData(d => ({ ...d, terminaly: d.terminaly.map(x => x.id === t.id ? { ...x, nazev: e.target.value } : x) }))}
                                onBlur={e => uprav(t, { nazev: e.target.value })}
                                className="text-sm rounded-lg px-2 py-1 font-semibold"
                                style={{ background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', width: isMobile ? '100%' : 170, minHeight: isMobile ? 40 : undefined }} />

                            <span className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: stav.barva }}>
                                <span style={{ width: 8, height: 8, borderRadius: 99, background: stav.barva }} />
                                {stav.text}
                            </span>

                            {sbalene.has(t.id) ? (
                                <span className="ml-auto text-[11px]" style={{ color: muted }}>
                                    {telefon ? 'telefon' : kiosek ? 'kiosek' : (t.stanice?.nazev || 'pokladna')} · kód <span className="font-mono">{t.kod}</span>
                                </span>
                            ) : (<>

                            {telefon ? (
                                <>
                                    <span className="text-[11px]" style={{ color: chybiOpravneni.length ? '#EF4444' : muted }}>
                                        {t.sparovany
                                            ? [zar.model, zar.baterie != null ? `baterie ${zar.baterie} %${zar.nabiji ? ' ⚡' : ''}` : null,
                                                chybiOpravneni.length ? `chybí: ${chybiOpravneni.join(', ')}` : 'oprávnění v pořádku']
                                                .filter(Boolean).join(' · ')
                                            : 'kód zadej v aplikaci FofrTelefon'}
                                    </span>
                                    <button type="button" onClick={() => !t.vychozi_sms && uprav(t, { vychozi_sms: true })}
                                        title="Přes výchozí telefon jdou automatické SMS (rozvoz, zkoušky) a volání z OS"
                                        className="flex items-center gap-1.5 text-[11px]" style={{ color: t.vychozi_sms ? 'var(--text-primary)' : muted, fontWeight: t.vychozi_sms ? 600 : 400, background: 'none', border: 'none', cursor: t.vychozi_sms ? 'default' : 'pointer', minHeight: 32 }}>
                                        <span style={{ width: 14, height: 14, borderRadius: 7, border: `2px solid ${t.vychozi_sms ? barva : border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {t.vychozi_sms && <span style={{ width: 6, height: 6, borderRadius: 3, background: barva }} />}
                                        </span>
                                        výchozí pro SMS
                                    </button>
                                    <span className="basis-full text-[11px]" style={{ color: muted }}>
                                        {(() => {
                                            const jeho = data.terminaly.filter(x => x.typ !== 'telefon' && x.telefon_id === t.id && x.aktivni);
                                            return jeho.length ? `Obsluhuje: ${jeho.map(x => x.nazev).join(', ')}` : 'Zatím ho nemá žádná pokladna — přiřaď ho u pokladny.';
                                        })()}
                                    </span>
                                </>
                            ) : kiosek ? (
                                <span className="text-[11px]" style={{ color: muted }}>
                                    {t.sparovany
                                        ? 'samoobslužný kiosek s lamami'
                                        : 'kód zadej na kiosku: Nastavení pokladny → Spustit jako kiosek'}
                                </span>
                            ) : (
                            <>
                                <label className="flex items-center gap-1.5 text-[11px]" style={{ color: muted }}>
                                    kuchyň
                                    <Dropdown value={t.kds_stanice_id ?? ''} isDark={isDark} height={30} minWidth={150}
                                        onChange={v => uprav(t, { kds_stanice_id: v ? parseInt(v) : null })}
                                        options={[
                                            { value: '', label: hlavni ? `${hlavni.nazev} (hlavní)` : 'Kuchyně 1 (hlavní)' },
                                            ...stanice.filter(x => x.id !== hlavni?.id).map(x => ({ value: x.id, label: x.nazev })),
                                        ]} />
                                </label>
                                <label className="flex items-center gap-1.5 text-[11px]" style={{ color: muted }}
                                    title="Přes tento telefon pokladna volá a posílá SMS a jen jeho hovory a SMS vidí">
                                    telefon
                                    <Dropdown value={t.telefon_id ?? ''} isDark={isDark} height={30} minWidth={150}
                                        onChange={v => uprav(t, { telefon_id: v ? parseInt(v) : null })}
                                        options={[{ value: '', label: 'Žádný' }, ...telefony.map(x => ({ value: x.id, label: x.nazev }))]} />
                                </label>
                                {t.telefon_id && (() => {
                                    // Sluchátka spárovaná s telefonem pokladny (hlásí FofrTelefon 1.4.0+)
                                    const tel = data.terminaly.find(x => x.id === t.telefon_id);
                                    const sluchatka = tel?.stav_zarizeni?.sluchatka || [];
                                    const options = [{ value: '', label: 'podle telefonu' },
                                        ...sluchatka.map(x => ({ value: x.adresa, label: `${x.nazev}${!x.pripojeno ? ' (nepřipojené)' : x.baterie != null ? ` · ${x.baterie} %` : ''}` }))];
                                    if (t.sluchatko && !sluchatka.some(x => x.adresa === t.sluchatko)) {
                                        options.push({ value: t.sluchatko, label: `${t.sluchatko} (telefon ho nehlásí)` });
                                    }
                                    return (
                                        <label className="flex items-center gap-1.5 text-[11px]" style={{ color: muted }}
                                            title="Hovor přijatý nebo vytočený z téhle pokladny telefon přepne do tohoto Bluetooth sluchátka">
                                            sluchátko
                                            <Dropdown value={t.sluchatko ?? ''} isDark={isDark} height={30} minWidth={150}
                                                onChange={v => uprav(t, { sluchatko: v || null })}
                                                options={options} />
                                        </label>
                                    );
                                })()}
                                <label className="flex items-center gap-1.5 text-[11px]" style={{ color: t.tiskne_bony ? 'var(--text-primary)' : muted }}
                                    title="Na tiskárně bonů téhle pokladny se tisknou kuchyňské bony celé pobočky (vždy jen jedna pokladna)">
                                    <input type="checkbox" checked={!!t.tiskne_bony} onChange={e => uprav(t, { tiskne_bony: e.target.checked })} />
                                    tiskne bony
                                </label>
                                <label className="flex items-center gap-1.5 text-[11px]" style={{ color: t.minutky ? 'var(--text-primary)' : muted, fontWeight: t.minutky ? 600 : 400 }}
                                    title="Lišta minutek v Kuchyni — předvolby podle kuchyně téhle pokladny (Správa → Minutky)">
                                    <input type="checkbox" checked={!!t.minutky} onChange={e => uprav(t, { minutky: e.target.checked })} />
                                    minutky
                                </label>
                                <button type="button" onClick={() => setZobrazeniOtevrene(o => o === t.id ? null : t.id)}
                                    title="Které záložky a tlačítka pokladna ukazuje"
                                    className="flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2"
                                    style={{ minHeight: 30, background: zobrazeniOtevrene === t.id ? 'rgba(59,130,246,0.12)' : 'none', border: `1px solid ${border}`, color: t.zobrazeni ? 'var(--text-primary)' : muted, cursor: 'pointer' }}>
                                    <LayoutGrid size={13} /> zobrazení{t.zobrazeni ? ` · ${t.zobrazeni.zalozky.length}/${ZALOZKY_POS.length}` : ''}
                                </button>
                            </>
                            )}

                            <div className="ml-auto flex items-center gap-2">
                                <span className="text-[11px]" style={{ color: muted }}>kód</span>
                                <span className="font-mono text-sm font-bold tracking-widest px-2 py-0.5 rounded"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: 'var(--text-primary)' }}>
                                    {t.kod}
                                </span>
                                <button title="Nový kód — odpojí spárovaný počítač (výměna, ztráta)"
                                    onClick={async () => { if (!t.sparovany || await potvrdit('Vygenerovat nový kód?', { text: `„${t.nazev}" se odpojí a bude se muset spárovat znovu.`, potvrdit: 'Nový kód' })) proved(() => api(`/api/pos-terminaly/${t.id}/novy-kod`, 'POST')); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}>
                                    <RefreshCw size={14} />
                                </button>
                                <button type="button" onClick={() => uprav(t, { aktivni: !t.aktivni })} title="Vyřazený terminál se chová jako neregistrovaný"
                                    className="flex items-center gap-1.5 text-[11px]" style={{ color: muted, background: 'none', border: 'none', cursor: 'pointer', minHeight: 32 }}>
                                    <span style={{ width: 30, height: 18, borderRadius: 9, position: 'relative', background: t.aktivni ? barva : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
                                        <span style={{ position: 'absolute', top: 2, left: t.aktivni ? 14 : 2, width: 14, height: 14, borderRadius: 7, background: '#fff' }} />
                                    </span>
                                    aktivní
                                </button>
                                {!isMobile && (
                                    <button title="Smazat"
                                        onClick={async () => { if (await potvrdit(`Smazat terminál „${t.nazev}"?`, { potvrdit: 'Smazat' })) proved(() => api(`/api/pos-terminaly/${t.id}`, 'DELETE')); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
                                        <Trash2 size={15} />
                                    </button>
                                )}
                            </div>

                            {zobrazeniOtevrene === t.id && !telefon && !kiosek && (
                                <ZobrazeniPokladny t={t} muted={muted} border={border} isDark={isDark}
                                    onZmena={zobrazeni => uprav(t, { zobrazeni })} />
                            )}

                            {t.sparovany && (
                                <RadekAktualizace t={t} verze={verze.filter(v => v.aplikace === (telefon ? 'telefon' : t.stav_zarizeni?.platforma === 'linux' ? 'pos-linux' : 'pos') && v.aktivni)}
                                    isDark={isDark} muted={muted} border={border}
                                    onZmena={zmena => uprav(t, zmena)}
                                    onAktualizovat={() => proved(() => api('/api/pos-terminaly/aktualizovat', 'POST', { terminaly: [t.id] }))} />
                            )}
                            </>)}
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap gap-2">
                <button onClick={() => pridat()}
                    className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                    style={{ background: barva, border: 'none', color: '#fff', cursor: 'pointer', minHeight: isMobile ? 44 : undefined }}>
                    <Plus size={13} /> Přidat terminál
                </button>
                <button onClick={pridatTelefon}
                    className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                    style={{ ...tlacitkoObrys, minHeight: isMobile ? 44 : undefined }}>
                    <Smartphone size={13} /> {maTelefon ? 'Přidat další telefon' : 'Přidat telefon bistra'}
                </button>
                <button onClick={pridatKiosek}
                    className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                    style={{ ...tlacitkoObrys, minHeight: isMobile ? 44 : undefined }}>
                    <Tablet size={13} /> {kiosky.length ? 'Přidat další kiosek' : 'Přidat kiosek'}
                </button>
            </div>

            {kiosky.length > 0 && (
                <KiosekNastaveni isDark={isDark} barva={barva} muted={muted} border={border} fieldBg={fieldBg} kartaBg={kartaBg} />
            )}

            {maTelefon && (
                <p className="text-xs" style={{ color: muted }}>
                    Aplikaci FofrTelefon stáhneš přímo v telefonu z adresy{' '}
                    <span className="font-mono" style={{ color: 'var(--text-primary)', userSelect: 'all' }}>{window.location.origin}/api/telefon/apk</span>.
                    Po instalaci v ní zadej kód telefonu a projdi povolení, která aplikace ukáže.
                </p>
            )}

            {data.odmitnute > 0 && (
                <button onClick={() => proved(() => api('/api/pos-terminaly/odmitnute', 'DELETE'))}
                    className="text-[11px] font-semibold" style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', padding: 0 }}>
                    Odmítnuté žádosti o registraci: {data.odmitnute} · zobrazit znovu
                </button>
            )}

            {data.nezname.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: muted }}>Čeká na registraci</h4>
                    {data.nezname.map(z => (
                        <div key={z.zarizeni_id} className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-2.5"
                            style={{ background: kartaBg, border: `1px dashed ${border}` }}>
                            <Monitor size={16} style={{ color: muted }} />
                            <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{z.zarizeni_id.slice(0, 8)}</span>
                            <span className="text-[11px]" style={{ color: muted }}>
                                {z.uzivatel ? `${z.uzivatel} · ` : ''}naposledy {new Date(z.posledni).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <button onClick={() => pridat(z.zarizeni_id)}
                                className="ml-auto flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1"
                                style={{ background: barva, color: '#fff', border: 'none', cursor: 'pointer', minHeight: isMobile ? 40 : undefined }}>
                                <Link2 size={12} /> Zaregistrovat
                            </button>
                            <button title="Zařízení se v seznamu už neukáže (spárovat kódem jde dál)"
                                onClick={() => proved(() => api('/api/pos-terminaly/odmitnout', 'POST', { zarizeni_id: z.zarizeni_id }))}
                                className="flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1"
                                style={{ background: 'none', color: '#EF4444', border: `1px solid ${border}`, cursor: 'pointer', minHeight: isMobile ? 40 : undefined }}>
                                <X size={12} /> Odmítnout
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <AplikaceVerze verze={verze} isDark={isDark} muted={muted} border={border} kartaBg={kartaBg}
                onZmena={nactiVerze}
                onAktualizovatVse={() => proved(() => api('/api/pos-terminaly/aktualizovat', 'POST', {}))} />

            <AplikaceTelefony isDark={isDark} muted={muted} border={border} kartaBg={kartaBg} />

            {novy && (
                <ModalShell onClose={() => setNovy(null)} isDark={isDark} nadpis={novy.typ === 'telefon' ? 'Nový telefon bistra' : novy.typ === 'kiosek' ? 'Nový kiosek' : novy.zarizeni_id ? 'Zaregistrovat zařízení' : 'Nový terminál'} ikona={novy.typ === 'telefon' ? Smartphone : novy.typ === 'kiosek' ? Tablet : Monitor} barva={barva}
                    paticka={(
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => setNovy(null)} className="rounded-lg"
                                style={{ height: 40, padding: '0 16px', fontSize: 13, flex: isMobile ? 1 : undefined, ...tlacitkoObrys, color: 'var(--text-secondary)' }}>Zrušit</button>
                            <button type="button" onClick={zalozit} className="rounded-lg font-semibold"
                                style={{ height: 40, padding: '0 16px', fontSize: 13, flex: isMobile ? 1 : undefined, background: barva, color: '#fff', border: 'none', cursor: 'pointer' }}>Založit</button>
                        </div>
                    )}>
                    <div style={{ padding: 16 }}>
                        <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Název</label>
                        <input autoFocus value={novy.nazev} onChange={e => setNovy(n => ({ ...n, nazev: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') zalozit(); }}
                            placeholder="Pokladna u pultu, Displej pizza…" className="w-full rounded-lg px-3 text-sm"
                            style={{ height: 40, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                        <p className="text-xs" style={{ color: muted, margin: '8px 0 0' }}>
                            {novy.typ === 'telefon'
                                ? 'Telefon dostane kód — ten zadáš v aplikaci FofrTelefon. Pak ho u pokladen vyber v poli „telefon".'
                                : novy.typ === 'kiosek'
                                    ? 'Kiosek dostane kód. Na počítači kiosku otevři FofrLamaPOS, v Nastavení pokladny zvol „Spustit jako kiosek" a kód zadej.'
                                    : 'Terminál dostane kód — ten opíšeš do pokladny a počítač se spáruje.'}
                        </p>
                    </div>
                </ModalShell>
            )}
            {potvrzeni}
        </div>
    );
}

/**
 * Aktualizace terminálu: jakou verzi má, kanál (stabilní / testovací),
 * připíchnutá verze (návrat k předchozí) a „Aktualizovat teď".
 */
function RadekAktualizace({ t, verze, isDark, muted, border, onZmena, onAktualizovat }) {
    const posledni = [...verze].filter(v => t.kanal_aktualizaci === 'testovaci' || v.kanal === 'stabilni')
        .sort((a, b) => b.verze.localeCompare(a.verze, undefined, { numeric: true }))[0];
    const cil = t.pozadovana_verze || posledni?.verze;
    const aktualni = t.verze_aplikace && cil && t.verze_aplikace === cil;
    return (
        <div className="basis-full flex flex-wrap items-center gap-2 pt-2 mt-1" style={{ borderTop: `1px dashed ${border}` }}>
            <Download size={13} style={{ color: muted }} />
            <span className="text-[11px]" style={{ color: muted }}>verze</span>
            <span className="font-mono text-[12px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: aktualni ? '#10b981' : 'var(--text-primary)' }}>
                {t.verze_aplikace || 'neznámá'}
            </span>
            {cil && !aktualni && <span className="text-[11px]" style={{ color: muted }}>→ {cil}</span>}
            <span className="text-[11px] ml-2" style={{ color: muted }}>kanál</span>
            <Dropdown value={t.kanal_aktualizaci || 'stabilni'} isDark={isDark} height={28} minWidth={120}
                onChange={v => onZmena({ kanal_aktualizaci: v })}
                options={[{ value: 'stabilni', label: 'Stabilní' }, { value: 'testovaci', label: 'Testovací' }]} />
            <span className="text-[11px]" style={{ color: muted }}>verze</span>
            <Dropdown value={t.pozadovana_verze || ''} isDark={isDark} height={28} minWidth={140}
                onChange={v => onZmena({ pozadovana_verze: v || null })}
                options={[{ value: '', label: 'Nejnovější' }, ...verze.map(v => ({ value: v.verze, label: `${v.verze}${v.kanal === 'testovaci' ? ' (test)' : ''}` }))]} />
            <button onClick={onAktualizovat} disabled={!t.online}
                title={t.online ? 'Terminál zkontroluje a nainstaluje verzi, jakmile nebude mít rozdělanou práci' : 'Terminál je offline'}
                className="ml-auto text-[11px] font-semibold rounded-lg px-2.5 py-1"
                style={{ background: 'transparent', border: `1px solid ${border}`, color: 'var(--text-primary)', cursor: t.online ? 'pointer' : 'not-allowed', opacity: t.online ? 1 : 0.5 }}>
                Aktualizovat teď
            </button>
        </div>
    );
}

/**
 * Zobrazení pokladny (2026-10-22) — které záložky a tlačítka POS ukazuje
 * a na které záložce startuje. Všechno zaškrtnuté = null (i budoucí záložky).
 * Lišta (2026-10-23): horní lišta POS schovaná s cípem, nebo stále vysunutá.
 */
function ZobrazeniPokladny({ t, muted, border, isDark, onZmena }) {
    const z = t.zobrazeni;
    const zalozky = z?.zalozky ?? ZALOZKY_POS.map(x => x.id);
    const tlacitka = z?.tlacitka ?? TLACITKA_POS.map(x => x.id);
    const vychozi = z?.vychozi && zalozky.includes(z.vychozi) ? z.vychozi : zalozky[0];
    const lista = z?.lista === 'stale' ? 'stale' : 'schovavat';
    const oblibene = ['vypnuto', 'zakaznik'].includes(z?.oblibene) ? z.oblibene : 'vsichni';

    const ulozit = (nZalozky, nTlacitka, nVychozi, nLista = lista, nOblibene = oblibene) => {
        if (nZalozky.length === 0) return;   // aspoň jedna záložka
        const vse = nZalozky.length === ZALOZKY_POS.length && nTlacitka.length === TLACITKA_POS.length && (nVychozi ?? 'stoly') === 'stoly' && nLista === 'schovavat' && nOblibene === 'vsichni';
        onZmena(vse ? null : {
            zalozky: ZALOZKY_POS.map(x => x.id).filter(id => nZalozky.includes(id)),
            tlacitka: TLACITKA_POS.map(x => x.id).filter(id => nTlacitka.includes(id)),
            vychozi: nZalozky.includes(nVychozi) ? nVychozi : nZalozky[0],
            lista: nLista,
            oblibene: nOblibene,
        });
    };
    const prepni = (seznam, id) => seznam.includes(id) ? seznam.filter(x => x !== id) : [...seznam, id];

    const Zaskrt = ({ zapnuto, onClick, children, zakazano }) => (
        <label className="flex items-center gap-1.5 text-xs" style={{ color: zapnuto ? 'var(--text-primary)' : muted, cursor: zakazano ? 'not-allowed' : 'pointer', minHeight: 30 }}>
            <input type="checkbox" checked={zapnuto} disabled={zakazano} onChange={onClick} />
            {children}
        </label>
    );

    return (
        <div className="w-full flex flex-col gap-2 rounded-lg px-3 py-2.5" style={{ border: `1px dashed ${border}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' }}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: 0.4, minWidth: 70 }}>Záložky</span>
                {ZALOZKY_POS.map(x => (
                    <Zaskrt key={x.id} zapnuto={zalozky.includes(x.id)}
                        zakazano={zalozky.length === 1 && zalozky.includes(x.id)}
                        onClick={() => ulozit(prepni(zalozky, x.id), tlacitka, vychozi)}>
                        {x.nazev}
                    </Zaskrt>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: 0.4, minWidth: 70 }}>Tlačítka</span>
                {TLACITKA_POS.map(x => (
                    <Zaskrt key={x.id} zapnuto={tlacitka.includes(x.id)} onClick={() => ulozit(zalozky, prepni(tlacitka, x.id), vychozi)}>
                        {x.nazev}
                    </Zaskrt>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: 0.4, minWidth: 70 }}>Horní lišta</span>
                <Dropdown value={lista} isDark={isDark} height={30} minWidth={220}
                    onChange={v => ulozit(zalozky, tlacitka, vychozi, v)}
                    options={[
                        { value: 'schovavat', label: 'Schovávat (stáhne se z cípu)' },
                        { value: 'stale', label: 'Stále vysunutá' },
                    ]} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: 0.4, minWidth: 70 }}>Oblíbené</span>
                <Dropdown value={oblibene} isDark={isDark} height={30} minWidth={260}
                    onChange={v => ulozit(zalozky, tlacitka, vychozi, lista, v)}
                    options={[
                        { value: 'vsichni', label: 'Zapnuto — nejprodávanější napříč' },
                        { value: 'zakaznik', label: 'Jen zákazníka — co si objednává on' },
                        { value: 'vypnuto', label: 'Vypnuto — vybrat kategorii nebo hledat' },
                    ]} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: 0.4, minWidth: 70 }}>Start na</span>
                <Dropdown value={vychozi} isDark={isDark} height={30} minWidth={150}
                    onChange={v => ulozit(zalozky, tlacitka, v)}
                    options={ZALOZKY_POS.filter(x => zalozky.includes(x.id)).map(x => ({ value: x.id, label: x.nazev }))} />
                {z && (
                    <button type="button" onClick={() => onZmena(null)} className="text-[11px] font-semibold"
                        style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', minHeight: 30 }}>
                        Zobrazit vše
                    </button>
                )}
                <span className="text-[11px]" style={{ color: muted }}>Telefon se ukáže jen pokladně s přiřazeným telefonem. Změna se v pokladně projeví do minuty.</span>
            </div>
        </div>
    );
}
